import {useParams} from "react-router-dom";
import React, {useEffect, useState, useDeferredValue, useRef} from "react";
import {Spinner, useBreakpointValue, useDisclosure, useToast} from "@chakra-ui/react";
import {io} from "socket.io-client";
import {useDispatch, useSelector} from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import { motion, AnimatePresence } from "framer-motion";
import {
    setCurrentGeneratePDF,
    setCurrentGeneratePDFPage,
    setCurrentGenerationSessionToken, setFilesInDocumentGeneration, setFilesInDocumentGenerationNative,
    setIsGeneratePDFVisible, setMessagesInDocumentGeneration, setMessagesInDocumentGenerationNative
} from "../../redux/reducers";
import {
    addActiveSourcesMultidoc,
    createMultidocSession, createMultidocSessionNativeChat,
    getMultidoc, getMultidocNative,
    removeSourceMultidoc
} from "../../utils/multi-doc-helpers";
import axios from "axios";
import {useAuth} from "../../redux/AuthProvider";
import {useServerIP} from "../../redux/ServerIPContext";
import PDFPageGenerationImage from "./PDFPageGenerationImage";
import PricingScreen from "../../components/Modals/Payments/PricingScreen";

export default function WrapperComponentAudiobook ()  {
    const auth = useAuth();
    const toast = useToast();
    const [socketGeneration, setSocketGeneration] = useState(null);
    const { id } = useParams();
    const deferredId = useDeferredValue(id); // Defer heavy operations
    const [sessionTokenGeneration, setSessionTokenGeneration] = useState("")
    const [currentGeneration, setCurrentGeneration] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const { serverIPs, setServerIPs, isDev } = useServerIP();

    // Guards to prevent duplicate API calls and socket connections
    const hasInitializedSession = useRef(false);
    const isCreatingSession = useRef(false);
    const currentIdRef = useRef(null);
    const socketRef = useRef(null);
    const breakpoint = useBreakpointValue({ base: 'base', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' });
    const isMobile = breakpoint === 'base' || breakpoint === 'sm';

    const generations = useSelector(state => state.documents.savedGenerations)
    // const documents = auth.isLoggedIn ? savedDocuments : auth.playgroundDocuments;

    const [forceRender, setForceRender] = useState(0);
    const [socketIDGeneration, setSocketIDGeneration] = useState("");
    const { isTypingGeneration, setIsTypingGeneration } = useAuth();
    const {isThinkingGeneration, setIsThinkingGeneration} = useAuth();
    const dispatch = useDispatch();
    const { isOpen: isOpenPricingScreen, onOpen: onOpenPricingScreen, onClose: onClosePricingScreen } = useDisclosure();

    const [loadingStep, setLoadingStep] = useState(0);
    const loadingMessages = [
        "We're preparing your workspace...",
        "We're loading up your images...",
        "Putting some final touches so you're ready to go!"
    ];

    // Reset guards when ID changes
    useEffect(() => {
        if (currentIdRef.current !== id) {
            console.log("Multidoc ID changed, resetting guards", currentIdRef.current, "->", id);

            // Clean up existing socket properly
            if (socketRef.current) {
                console.log("Cleaning up existing socket for ID change");
                socketRef.current.off(); // Remove all listeners
                socketRef.current.disconnect();
                socketRef.current = null;
            }

            currentIdRef.current = id;
            hasInitializedSession.current = false;
            isCreatingSession.current = false;
            setSessionTokenGeneration("");
            setSocketGeneration(null);
            setSocketIDGeneration("");
        }
    }, [id]);

    // Initialize socket connection with deferred ID for better performance
    useEffect( () => {
        if (!deferredId) return;

        console.log("In UseEffect WrapperComponentGeneration with id:", deferredId);

        // Use a timeout to defer socket connection and not block initial render
        const initTimer = setTimeout(() => {
            // Prevent multiple socket connections
            if (socketRef.current) {
                console.log("Socket already exists, not creating new one");
                return;
            }

            const userID = auth.isLoggedIn ? localStorage.getItem("userID") : "11111111-1111-1111-1111-111111111111"
            const newSocketGeneration = io(serverIPs.SERVER_IP_SOCKET, {
                query: { user_id: userID }
            });

            socketRef.current = newSocketGeneration;
            setSocketGeneration(newSocketGeneration);
            console.log("Socket Generation:", socketGeneration)
            newSocketGeneration.on('connected', function(data) {
                console.log("Connected Generation:", data);
                setSocketIDGeneration("user_" + userID);
                setIsInitializing(false);
            });
        }, 0); // Defer to next tick

        return () => {
            clearTimeout(initTimer);
            // Don't disconnect socket on every useEffect cleanup - only on unmount
            console.log("Socket useEffect cleanup - keeping socket connected");
        }
    }, [deferredId, serverIPs.SERVER_IP_SOCKET, auth.isLoggedIn])

    useEffect(() => {
        console.log("In UseEffect WrapperComponentGeneration with socket:", socketGeneration);
        console.log("Checking generations for ID:", id, "Available generations:", generations?.length);

        // Only run if we don't already have a currentGeneration set for this ID
        if (currentGeneration?.id === id) {
            console.log("Already have currentGeneration for this ID, skipping");
            return;
        }

        if (generations && generations.length > 0) {
            console.log("Generations length > 0")
            const foundGeneration = generations.find((generation) => generation.id === id);
            console.log("Found generation:", foundGeneration);

            if (foundGeneration) {
                setCurrentGeneration(foundGeneration);
                // Reset PDF state if this generation has no files
                if (!foundGeneration.files || foundGeneration.files.length === 0) {
                    dispatch(setCurrentGeneratePDF(""));
                    dispatch(setCurrentGeneratePDFPage(1));
                    dispatch(setIsGeneratePDFVisible(false));
                }
                setForceRender(prevForceRender => prevForceRender + 1);
            } else if (!currentGeneration) {
                // Only create placeholder if we don't have any currentGeneration
                console.log("Generation not found in Redux, creating placeholder");
                const placeholderGeneration = {
                    id: id,
                    name: "Images Workspace",
                    messages: [],
                    files: [],
                    lastOpenTime: Date.now()
                };
                setCurrentGeneration(placeholderGeneration);
                // Reset PDF state for new placeholder workspace
                dispatch(setCurrentGeneratePDF(""));
                dispatch(setCurrentGeneratePDFPage(1));
                dispatch(setIsGeneratePDFVisible(false));
                setForceRender(prevForceRender => prevForceRender + 1);
            }
        } else if (id && !currentGeneration) {
            // Handle case where there are no generations yet but we have an ID (new workspace)
            console.log("No generations in Redux yet, creating placeholder for new workspace");
            const placeholderGeneration = {
                id: id,
                name: "Images Workspace",
                messages: [],
                files: [],
                lastOpenTime: Date.now()
            };
            setCurrentGeneration(placeholderGeneration);
            // Reset PDF state for new placeholder workspace
            dispatch(setCurrentGeneratePDF(""));
            dispatch(setCurrentGeneratePDFPage(1));
            dispatch(setIsGeneratePDFVisible(false));
            setForceRender(prevForceRender => prevForceRender + 1);
        }
    }, [generations.length, id]); // Only depend on length to reduce re-runs

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    useEffect(() => {
        console.log("In UseEffect WrapperComponentGeneration with currentGeneration:", currentGeneration);

        // Guard against duplicate calls - removed sessionToken check to always fetch new token
        if (!currentGeneration || hasInitializedSession.current || isCreatingSession.current) {
            console.log("Skipping session creation:", {
                hasCurrentGeneration: !!currentGeneration,
                hasInitialized: hasInitializedSession.current,
                isCreating: isCreatingSession.current
            });
            return;
        }

        console.log("CurrentGeneration is not null, creating session");
        // Reset session token to ensure fresh session
        setSessionTokenGeneration("");
        isCreatingSession.current = true;

        const fetchSessionTokenGeneration = async () => {
            try {
                const start = new Date().getTime() / 1000;
                const userID = auth.isLoggedIn ? localStorage.getItem("userID") : "11111111-1111-1111-1111-111111111111"
                console.log("USERID", userID)

                // let createResponse1 = await createMultidocSessionNativeChat(id, userID, serverIPs.SERVER_IP_MULTIDOC);

                // Retrieving and validating
                const storedData = localStorage.getItem("createMultidocSessionNativeChat");
                const ONE_MINUTE = 60000;

                let progressKey = null;
                let sessionToken = null;
                let isValid = false;

                if (storedData) {
                    try {
                        const parsed = JSON.parse(storedData);
                        isValid = parsed.timestamp &&
                            parsed.progressKey &&
                            parsed.sessionToken &&
                            ((Date.now() - parsed.timestamp) < ONE_MINUTE);

                        if (isValid) {
                            progressKey = parsed.progressKey;
                            sessionToken = parsed.sessionToken;
                            console.log("Using cached session data");
                        }
                    } catch (e) {
                        console.warn("Invalid cached data");
                    }
                }

                if (!isValid) {
                    // Create new session
                    const r = await createMultidocSessionNativeChat(id, userID, serverIPs.SERVER_IP_MULTIDOC);
                    console.log("createMultidocSessionNativeChat in WRAPPER COMPONENT", r);

                    const dataWithTimestamp = {
                        progressKey: r.progressKey,
                        sessionToken: r.multidocSessionToken,
                        timestamp: Date.now()
                    };

                    localStorage.setItem("createMultidocSessionNativeChat", JSON.stringify(dataWithTimestamp));

                    progressKey = r.progressKey;
                    sessionToken = r.multidocSessionToken;
                }

                // poll every 3 seconds
                while (true) {
                    try {
                        console.log("CALLING GENERAL_PROGRESS with key:", progressKey);
                        const response = await axios.post(serverIPs.SERVER_IP + "/general_progress", {
                            key: progressKey
                        });
                        console.log("GENERAL_PROGRESS response:", response);
                        if (response.data.message.status === "completed") {
                            // console.log("RESPONSE FROM CREATE MULTIDOC SESSION", createResponse1);
                            const end1 = new Date().getTime() / 1000;
                            const duration1 = end1 - start;
                            console.log("DURATION OF CREATE SESSION RIGHT AWAY", duration1);
                            const getResponse = await getMultidocNative(id, userID, serverIPs.SERVER_IP_MULTIDOC);
                            if (getResponse) {
                                console.log("RESPONSE FROM GET MULTIDOC", getResponse);
                                if (getResponse.messages) {
                                    dispatch(setMessagesInDocumentGeneration({documentID: currentGeneration.id, newMessages: getResponse.messages}));
                                }
                                if (getResponse.files) {
                                    const files = getResponse.files.map((file) => {
                                        return {
                                            id: file.file_id,
                                            name: file.file_name,
                                            url: file.url,
                                            isActive: true,
                                        }
                                    });
                                    dispatch(setFilesInDocumentGeneration({documentID: currentGeneration.id, newFiles: files}));
                                    console.log("MULTIDOC SESSION TOKEN DEBUGGING", sessionToken)

                                    const startActive = new Date().getTime() / 1000;
                                    addActiveSourcesMultidoc(sessionToken, localStorage.getItem("userID"),
                                        files.map((file) => file.id), serverIPs.SERVER_IP_MULTIDOC).then(r => {
                                        const endActive = new Date().getTime() / 1000;
                                        const durationActive = endActive - startActive;
                                        console.log("DURATION OF ADD ACTIVE SOURCES", durationActive);
                                    });

                                    if (getResponse.files.length > 0) {
                                        dispatch(setCurrentGeneratePDF(getResponse.files[0].url));
                                        dispatch(setCurrentGeneratePDFPage(1));
                                        dispatch(setIsGeneratePDFVisible(true));
                                    } else {
                                        // Reset PDF state for workspaces with no files
                                        dispatch(setCurrentGeneratePDF(""));
                                        dispatch(setCurrentGeneratePDFPage(1));
                                        dispatch(setIsGeneratePDFVisible(false));
                                    }
                                }
                                setSessionTokenGeneration(sessionToken);
                                const end2 = new Date().getTime() / 1000;
                                const duration2 = end2 - start;
                                console.log("DURATION OF CREATE SESSION LATER", duration2);
                                dispatch(setCurrentGenerationSessionToken(sessionToken));

                                // Mark as initialized to prevent duplicate calls
                                hasInitializedSession.current = true;
                            }


                            return response.data.message;
                        }
                    } catch (error) {
                        console.error("Error in general_progress", error);
                    }
                    await sleep(3000);
                }
            } catch (error) {
                console.error("Error in fetchSessionTokenGeneration:", error);
            } finally {
                isCreatingSession.current = false;
            }
        };

        fetchSessionTokenGeneration();
    }, [currentGeneration?.id, id]); // Only depend on the ID, not the whole object

    // Component mount - ensure fresh session on every load
    useEffect(() => {
        console.log("Component mounting, resetting session state");
        // Only reset session token on mount, not during normal operation
        if (sessionTokenGeneration !== "") {
            setSessionTokenGeneration("");
        }
        hasInitializedSession.current = false;
        isCreatingSession.current = false;

        // Reset PDF state to prevent leftover documents from other workspaces
        dispatch(setCurrentGeneratePDF(""));
        dispatch(setCurrentGeneratePDFPage(1));
        dispatch(setIsGeneratePDFVisible(false));
    }, []);

    // Component unmount cleanup
    useEffect(() => {
        return () => {
            console.log("Component unmounting, cleaning up socket");
            if (socketRef.current) {
                socketRef.current.off(); // Remove all listeners
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (sessionTokenGeneration === "") {
            const timer = setInterval(() => {
                setLoadingStep((prev) => (prev + 1) % loadingMessages.length);
            }, 3000); // Change message every 3 seconds
            return () => clearInterval(timer);
        }
    }, [sessionTokenGeneration]);

    const LoadingScreen = () => (
        <div style={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
            paddingTop: "0rem",
            background: 'linear-gradient(135deg, #f6f8ff 0%, #f1f5ff 100%)'
        }}>
            <motion.div
                animate={{
                    scale: [1, 1.2, 1],
                }}
                transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            >
                <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    border: '3px solid transparent',
                    borderTopColor: '#3182ce',
                    borderBottomColor: '#3182ce',
                    animation: 'spin 1.5s linear infinite'
                }}>
                    <style>
                        {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
                    </style>
                </div>
            </motion.div>

            <AnimatePresence mode='wait'>
                <motion.div
                    key={loadingStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    style={{
                        marginTop: '4rem',
                        marginBottom: '1rem',
                        fontSize: '1.25rem',
                        fontWeight: '500',
                        color: '#2d3748',
                        textAlign: 'center',
                        maxWidth: '80%'
                    }}
                >
                    {loadingMessages[loadingStep]}
                </motion.div>
            </AnimatePresence>

            <motion.div
                animate={{
                    opacity: [0.5, 1, 0.5],
                }}
                transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
                style={{
                    marginTop: '1rem',
                    display: 'flex',
                    gap: '0.5rem'
                }}
            >
                {[...Array(3)].map((_, i) => (
                    <div
                        key={i}
                        style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: i === loadingStep ? '#3182ce' : '#CBD5E0'
                        }}
                    />
                ))}
            </motion.div>
        </div>
    );

    return (
        sessionTokenGeneration === "" ?
            // <div style={{justifyContent:'center', alignItems:'center', display:'flex', height:'100%', width:'100%', paddingTop:"5rem"}}>
            //     <MySpinnerLarge/>
            // </div>
            <LoadingScreen/>
            :
            <div>
                <PDFPageGenerationImage documentGeneration={generations.find((generation) => generation.id === id)}
                                        isMobile={isMobile}
                                        key={forceRender}
                                        documentsGeneration={generations}
                                        idGeneration={id}
                                        isLoggedInGeneration={auth.isLoggedIn}
                                        sessionTokenGeneration={sessionTokenGeneration}
                                        socketIDGeneration={socketIDGeneration}
                                        socketGeneration={socketGeneration}
                                        isTypingGeneration={isTypingGeneration} setIsTypingGeneration={setIsTypingGeneration}
                                        isThinkingGeneration={isThinkingGeneration} setIsThinkingGeneration={setIsThinkingGeneration}
                                        toastGeneration={toast}/>
                <PricingScreen isOpenPricingScreen={isOpenPricingScreen}
                               onClosePricingScreen={onClosePricingScreen}
                               onOpenPricingScreen={onOpenPricingScreen}/>
            </div>
    )
}







